import { DatePipe, NgClass, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { showErrorSwal } from '@asamco/utils';
import { DxDataGridModule, DxPopupModule } from 'devextreme-angular';
import { DxiColumnModule } from 'devextreme-angular/ui/nested';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { InvoicePdfDeliveryRunStatus, PendingInvoicePdfDelivery, RetryInvoicePdfDeliveriesResult } from '../../interfaces/pending-invoice-pdf-delivery.interface';
import { InvoicePdfDeliveryService } from '../../services/invoice-pdf-delivery.service';

@Component({
	selector: 'al-link-invoice-pdf-deliveries',
	templateUrl: './link-invoice-pdf-deliveries.component.html',
	styleUrls: ['./link-invoice-pdf-deliveries.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		DatePipe,
		NgClass,
		NgIf,
		DxDataGridModule,
		DxPopupModule,
		DxiColumnModule,
	],
})
export class LinkInvoicePdfDeliveriesComponent {
	@Input({ required: true })
	linkId!: number;

	@Output()
	pendingCountChange = new EventEmitter<number>();

	deliveries: PendingInvoicePdfDelivery[] = [];
	runStatus: InvoicePdfDeliveryRunStatus | null = null;
	selectedDeliveryIds: number[] = [];
	loading = false;
	bulkActionRunning = false;
	visible = false;
	private actionDeliveryIds = new Set<number>();
	private refreshVersion = 0;

	constructor(
		private invoicePdfDeliveryService: InvoicePdfDeliveryService,
		private changeDetectorRef: ChangeDetectorRef
	) {}

	open(): void {
		this.visible = true;
		void this.refresh();
	}

	close(): void {
		this.visible = false;
		this.refreshVersion++;
		this.loading = false;
		this.changeDetectorRef.markForCheck();
	}

	async refresh(): Promise<void> {
		const version = ++this.refreshVersion;
		this.loading = true;
		this.changeDetectorRef.markForCheck();
		try {
			const [deliveries, runStatus] = await Promise.all([
				firstValueFrom(this.invoicePdfDeliveryService.getPending(this.linkId)),
				firstValueFrom(this.invoicePdfDeliveryService.getRunStatus(this.linkId)),
			]);
			if (version === this.refreshVersion) {
				this.deliveries = deliveries;
				this.runStatus = runStatus;
				this.selectedDeliveryIds = [];
				this.pendingCountChange.emit(deliveries.length);
				if (deliveries.length === 0) {
					this.visible = false;
				}
			}
		} catch (error) {
			if (version === this.refreshVersion) {
				showErrorSwal(error);
			}
		} finally {
			if (version === this.refreshVersion) {
				this.loading = false;
				this.changeDetectorRef.markForCheck();
			}
		}
	}

	async retry(delivery: PendingInvoicePdfDelivery): Promise<void> {
		this.actionDeliveryIds.add(delivery.id);
		this.changeDetectorRef.markForCheck();
		try {
			await this.retryDeliveries([delivery.id], false, `Retry the PDF for source invoice ${delivery.sourceInvoiceId}?`);
		} finally {
			this.actionDeliveryIds.delete(delivery.id);
			this.changeDetectorRef.markForCheck();
		}
	}

	async retrySelected(): Promise<void> {
		if (this.selectedDeliveryIds.length === 0) {
			return;
		}

		await this.retryDeliveries(
			this.selectedDeliveryIds,
			false,
			`Queue ${this.selectedDeliveryIds.length} selected PDF ${this.selectedDeliveryIds.length === 1 ? 'delivery' : 'deliveries'} now?`
		);
	}

	async retryAll(): Promise<void> {
		await this.retryDeliveries([], true, `Queue all ${this.deliveries.length} outstanding PDF deliveries now?`);
	}

	onSelectionChanged(event: { selectedRowKeys?: number[] }): void {
		this.selectedDeliveryIds = event.selectedRowKeys ?? [];
		this.changeDetectorRef.markForCheck();
	}

	async cancel(delivery: PendingInvoicePdfDelivery): Promise<void> {
		const confirmation = await Swal.fire({
			icon: 'warning',
			title: 'Cancel invoice PDF delivery?',
			text: `The PDF for source invoice ${delivery.sourceInvoiceId} will no longer be retried.`,
			showCancelButton: true,
			confirmButtonText: 'Cancel delivery',
			confirmButtonColor: '#dc3545',
		});
		if (!confirmation.isConfirmed) {
			return;
		}

		this.actionDeliveryIds.add(delivery.id);
		this.changeDetectorRef.markForCheck();
		try {
			await firstValueFrom(this.invoicePdfDeliveryService.cancel(this.linkId, delivery.id));
			await this.refresh();
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.actionDeliveryIds.delete(delivery.id);
			this.changeDetectorRef.markForCheck();
		}
	}

	isActionRunning(deliveryId: number): boolean {
		return this.loading || this.bulkActionRunning || this.actionDeliveryIds.has(deliveryId);
	}

	get failedCount(): number {
		return this.deliveries.filter(delivery => delivery.attemptCount > 0 || delivery.status === 'Exhausted').length;
	}

	get processingCount(): number {
		return this.deliveries.filter(delivery => delivery.status === 'Processing').length;
	}

	get latestRunLabel(): string {
		if (!this.runStatus || this.runStatus.status === 'NotQueued') {
			return 'No run queued';
		}

		return this.runStatus.batchStatus
			? `${this.runStatus.status} / ${this.runStatus.batchStatus}`
			: this.runStatus.status;
	}

	get latestRunClass(): string {
		const status = this.runStatus?.batchStatus ?? this.runStatus?.status ?? 'NotQueued';
		return `run-status--${status.toLowerCase()}`;
	}

	private async retryDeliveries(deliveryIds: number[], all: boolean, confirmationText: string): Promise<void> {
		const confirmation = await Swal.fire({
			icon: 'question',
			title: all ? 'Retry all invoice PDFs?' : 'Retry invoice PDFs?',
			text: confirmationText,
			showCancelButton: true,
			confirmButtonText: 'Queue retry',
		});
		if (!confirmation.isConfirmed) {
			return;
		}

		this.bulkActionRunning = true;
		this.changeDetectorRef.markForCheck();
		try {
			const result = all
				? await firstValueFrom(this.invoicePdfDeliveryService.retryAll(this.linkId))
				: await firstValueFrom(this.invoicePdfDeliveryService.retryMany(this.linkId, deliveryIds));
			this.showRetryResult(result);
			await this.refresh();
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.bulkActionRunning = false;
			this.changeDetectorRef.markForCheck();
		}
	}

	private showRetryResult(result: RetryInvoicePdfDeliveriesResult): void {
		void Swal.fire({
			icon: result.queued > 0 ? 'success' : 'warning',
			title: result.queued > 0 ? `${result.queued} PDF ${result.queued === 1 ? 'delivery' : 'deliveries'} queued` : 'No deliveries queued',
			text: result.skipped > 0 ? `${result.skipped} deliveries were skipped because they are already processing or no longer pending.` : undefined,
			timer: 2500,
			showConfirmButton: false,
		});
	}
}
